/**
 * PRD.305 §8/§9 — the clinical Labs surface, shared by Patient Workspace Labs
 * and Consultation Labs.
 *
 * Both read the SAME canonical orders/tests/results as the Laboratory module;
 * what differs is emphasis and capability, not data. PRD §17 asks the clinical
 * surfaces to prioritise readability over the operational queue, so this
 * renders a chronological clinical list rather than a worklist table.
 *
 * What the surface offers is decided entirely by the resolved capabilities:
 *
 *   Published Results Only → published results, nothing else
 *   Monitor + Results      → the above plus in-progress orders and statuses
 *   Full Management        → the above plus New Order and the operational drawer
 *
 * ...each already intersected with RBAC. This component never re-derives that;
 * it just renders what it is handed, which is why the three modes cannot drift
 * apart between the two surfaces.
 */
import { useMemo, useState } from 'react';
import { Button } from '@helios/ui/button';
import {
  aggregateLabOrderStatus,
  filterOrdersForEncounter,
  type LabOrderRow,
  type LaboratoryCapabilities,
} from '@helios/data-access';
import { LabStatusBadge, LabEmptyState, formatLabDate, parseTestStatuses } from './lab-shared';
import { LabOrderDrawer } from './LabOrderDrawer';
import { CreateLabOrderDrawer, type LabOrderContext } from './CreateLabOrderDrawer';

interface Props {
  orders: LabOrderRow[];
  loaded: boolean;
  capabilities: LaboratoryCapabilities;
  /** Ordering context; omit to hide the New Order action entirely. */
  orderContext?: LabOrderContext | null;
  source: 'patient_workspace' | 'consultation';
  /** When set, only orders raised in this consultation are shown. */
  encounterId?: string | null;
  /** Compact styling for the patient sidebar panel. */
  dense?: boolean;
  /** Whether the current user may add ordered tests to an invoice. */
  canBill?: boolean;
}

export function LabsSurface({
  orders,
  loaded,
  capabilities,
  orderContext = null,
  source,
  encounterId = null,
  dense = false,
  canBill = false,
}: Props) {
  const [selected, setSelected] = useState<LabOrderRow | null>(null);
  const [creating, setCreating] = useState(false);

  const scoped = useMemo(
    () => (encounterId ? filterOrdersForEncounter(orders, encounterId) : orders),
    [orders, encounterId],
  );

  const rows = useMemo(
    () =>
      scoped.map((o) => ({
        order: o,
        aggregate: aggregateLabOrderStatus(parseTestStatuses(o.test_statuses)),
      })),
    [scoped],
  );

  // Published Results Only must not leak operational work — an order with
  // nothing published yet simply isn't part of this surface.
  const visible = useMemo(
    () => (capabilities.viewOperational ? rows : rows.filter((r) => r.aggregate === 'published')),
    [rows, capabilities.viewOperational],
  );

  const selectedLive = useMemo(
    () => (selected ? (orders.find((o) => o.id === selected.id) ?? selected) : null),
    [orders, selected],
  );

  if (!capabilities.viewPublished && !capabilities.viewOperational) {
    return (
      <LabEmptyState
        title="Laboratory is not available"
        description="You don’t have access to laboratory results for this patient."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {capabilities.createOrder && orderContext && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="gradient" size="sm" onClick={() => setCreating(true)}>
            New Lab Order
          </Button>
        </div>
      )}

      {!loaded ? (
        <div
          style={{
            padding: 'var(--sp-4)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Loading…
        </div>
      ) : visible.length === 0 ? (
        <LabEmptyState
          title={capabilities.viewOperational ? 'No laboratory orders' : 'No published results'}
          description={
            encounterId
              ? 'Laboratory work ordered during this consultation will appear here.'
              : capabilities.viewOperational
                ? 'This patient has no laboratory orders yet.'
                : 'Results appear here once the laboratory publishes them.'
          }
          action={
            capabilities.createOrder && orderContext ? (
              <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
                Order a lab test
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {visible.map(({ order, aggregate }) => (
            <button
              key={order.id}
              type="button"
              onClick={() => setSelected(order)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                width: '100%',
                textAlign: 'left',
                padding: dense ? 'var(--sp-2)' : 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {order.order_number ?? 'Lab order'} · {Number(order.test_count ?? 0)} test
                  {Number(order.test_count ?? 0) === 1 ? '' : 's'}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {formatLabDate(order.ordered_at)}
                  {order.provider_name ? ` · ${order.provider_name}` : ''}
                </span>
              </span>
              <LabStatusBadge status={aggregate} />
            </button>
          ))}
        </div>
      )}

      <LabOrderDrawer
        open={selectedLive != null}
        onOpenChange={(open) => !open && setSelected(null)}
        order={selectedLive}
        capabilities={capabilities}
      />

      {orderContext && (
        <CreateLabOrderDrawer
          open={creating}
          onOpenChange={setCreating}
          context={orderContext}
          source={source}
          canBill={canBill}
        />
      )}
    </div>
  );
}

export default LabsSurface;
